import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AdminNavbar from "./AdminNavbar";
import api from "lib/api/api";

const statusColors = {
  PENDING: "warning",
  SUCCEEDED: "success",
  FAILED: "error",
  CREATED: "default",
};

const AdminBankTransferPaymentsPage = () => {
  const [status, setStatus] = useState("PENDING");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPayments = async () => {
    setLoading(true);
    setError("");
    try {
      const query = status ? `?status=${status}` : "";
      const response = await api.get(`/admin/payments/bank-transfer${query}`);
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load bank transfers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const confirmPayment = async (transactionId) => {
    setError("");
    try {
      await api.post(`/admin/payments/bank-transfer/${transactionId}/confirm`);
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not confirm payment");
    }
  };

  const rejectPayment = async (transactionId) => {
    setError("");
    try {
      await api.post(`/admin/payments/bank-transfer/${transactionId}/reject`, null, {
        params: { reason: "Rejected from admin bank transfer page" },
      });
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || "Could not reject payment");
    }
  };

  return (
    <div className="admin-dashboard-layout">
      <AdminNavbar />
      <Container className="admin-dashboard-content">
        <Box sx={{ my: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Typography variant="h4" fontWeight={800}>
              Bank Transfer Payments
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="SUCCEEDED">Confirmed</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.transactionId}>
                    <TableCell>{payment.createdAt ? new Date(payment.createdAt).toLocaleString() : "-"}</TableCell>
                    <TableCell>{payment.studentName}</TableCell>
                    <TableCell>{payment.studentEmail}</TableCell>
                    <TableCell>{payment.courseTitle}</TableCell>
                    <TableCell>{payment.transferType}</TableCell>
                    <TableCell>{payment.currency} {Number(payment.amount || 0).toFixed(2)}</TableCell>
                    <TableCell sx={{ maxWidth: 240, wordBreak: "break-word" }}>{payment.paymentReference}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColors[payment.status] || "default"} label={payment.status} />
                    </TableCell>
                    <TableCell>
                      {payment.receiptFilePath ? (
                        <Button
                          href={`${process.env.REACT_APP_API_URL}${payment.receiptFilePath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          View
                        </Button>
                      ) : "-"}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={payment.status !== "PENDING" || loading}
                          onClick={() => confirmPayment(payment.transactionId)}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={payment.status !== "PENDING" || loading}
                          onClick={() => rejectPayment(payment.transactionId)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {!payments.length && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {loading ? "Loading..." : "No bank transfer payments found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
    </div>
  );
};

export default AdminBankTransferPaymentsPage;
